/**
 * What each metric on the quality page measures — the single copy of that wording.
 *
 * The failure mode of a metrics page is not a wrong number but a right number under a label
 * meaning something else, so the tooltips read from here rather than carrying their own
 * strings. Client-safe because the query layer is `server-only`.
 *
 * Two kinds of metric sit on this page and must never be described as one:
 *
 * - **phase-attributed** — per actor inside a session, so a session both sides handled
 *   contributes its AI phase to AI and its human phase to Team.
 * - **cohort** — per session, and the whole session belongs to whoever handled it.
 */

import {
  RESOLVED_OUTCOMES,
  REQUEST_OUTCOMES,
  SESSION_OUTCOMES,
  type Cohort,
  type CohortOutcomeCounts,
  type OutcomeCounts,
} from "./outcomes"

/** Nothing measured yet reads as «—», never as a zero the operator would trust. */
export const NO_VALUE = "—"

/** What the page measures over, said in the page's own words. */
export const SERVICE_METRIC_POPULATION =
  "Finalized sessions that received at least one customer message"

/** The denominator both rates divide by. */
export const AI_HANDLED_POPULATION = "AI-answered sessions"

export const ACTOR_LABELS = { ai: "AI", team: "Your team" } as const

export const COHORT_LABELS: Record<Cohort, string> = {
  aiOnly: "The agent alone",
  teamInvolved: "Your team took part",
  unattended: "Nobody answered",
}

/** What each cohort holds, for the split that names them. */
export const COHORT_DEFINITIONS: Record<Cohort, string> = {
  aiOnly: "The agent carried the request from the first message to the last, with nobody from your team writing.",
  teamInvolved: "Somebody from your team wrote at some point — the agent may have answered there too.",
  unattended: "Neither the agent nor your team wrote at all.",
}

/** How each half is attributed — the captions the two comparison groups carry. */
export const ATTRIBUTION_CAPTIONS = {
  phase: "Per phase — a session both sides handled counts in both columns",
  cohort: "Per session — the whole session belongs to whoever handled it",
} as const

export type ServiceMetricKey =
  | "firstResponse"
  | "replyTime"
  | "handleTime"
  | "customerEffort"
  | "transferRate"
  | "escalationRate"
  | "operatorTakeovers"
  | "pickedUp"
  | "escalationsHandled"
  | "timeToHuman"
  | "answeredByEmail"

export const SERVICE_METRIC_DEFINITIONS: Record<
  ServiceMetricKey,
  { label: string; definition: string; /** Which way is the better half, where the number alone does not say. */ direction?: string }
> = {
  firstResponse: {
    label: "First response, median",
    definition:
      "Median wait for the first reply from that side. After a handover the team's clock starts at the handover.",
  },
  replyTime: {
    label: "Reply time, median",
    definition:
      "Median of each session's average wait for a reply from that side. A run of customer messages counts once.",
  },
  handleTime: {
    label: "Handling time, median",
    definition:
      "Median time from the customer's first message to the session's last, over sessions that side handled end to end.",
  },
  customerEffort: {
    label: "Customer messages per session, average",
    definition: "How many messages a customer had to send before the session ended, on average.",
    direction: "fewer means the customer got there with less work",
  },
  transferRate: {
    label: "Transfer rate",
    definition:
      "AI-answered sessions that reached a person: the agent handed over, or an operator stepped in.",
  },
  escalationRate: {
    label: "Escalation rate",
    definition: "AI-answered sessions where the agent itself asked for a person.",
  },
  operatorTakeovers: {
    label: "Operator takeovers",
    definition: "Sessions the agent was answering when a person joined without being asked.",
  },
  pickedUp: {
    label: "Escalations picked up",
    definition: "Escalated sessions a person replied to, in the conversation or by email in the customer's thread.",
  },
  escalationsHandled: {
    label: "Escalations handled",
    definition: "Handovers this person answered.",
  },
  timeToHuman: {
    label: "Time to first human reply",
    definition: "Median wait from the handover to the team's first message in the conversation, over escalations answered there.",
  },
  answeredByEmail: {
    label: "by email",
    definition:
      "Your mailbox replied in the customer's email thread after the handover. Most likely the answer, but the email is not linked to the conversation.",
  },
}

/** A share of a population, or null when the population is empty — never a misleading zero. */
export function share(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null
}

/** How many of how many a share is — what its tile says beside the percentage. */
export interface ShareCounts {
  of: number;
  over: number;
}

/** Below this a question is an anecdote, not a pattern worth teaching the agent. */
export const UNRESOLVED_TOPIC_MIN_SESSIONS = 3

/** Below this in a window, a session or two would swing a share: too few to compare it with another. */
export const MIN_SHARE_POPULATION = 5

export type OutcomeMetricKey =
  | "selfServiceShare"
  | "resolutionRate"
  | "outcomes"
  | "byCohort"
  | "handoverOutcomes"
  | "escalationCategories"
  | "takeoverReasons"
  | "needsReview"
  | "unresolvedTopics"

export const OUTCOME_METRIC_DEFINITIONS: Record<
  OutcomeMetricKey,
  { label: string; definition: string }
> = {
  selfServiceShare: {
    label: "Resolved by the agent",
    definition: "Real requests the agent resolved on its own, out of all judged real requests.",
  },
  resolutionRate: {
    label: "Left to the agent",
    definition:
      "Resolved requests out of those left to the agent to finish — sessions your team took part in or was handed are not counted.",
  },
  outcomes: {
    label: "How sessions ended",
    definition:
      "Every judged real request by its outcome. Story mentions, bots and lone reactions, and threads your team started itself, are counted apart and left out of every rate.",
  },
  byCohort: {
    label: "Who handled it",
    definition:
      "Every request by who carried it: the agent alone, your team took part, or nobody answered at all.",
  },
  handoverOutcomes: {
    label: "What happened after",
    definition: "How your team's part ended, for sessions handed to it.",
  },
  escalationCategories: {
    label: "Why the agent handed over",
    definition: "Sessions handed to your team, by the reason found in the conversation.",
  },
  takeoverReasons: {
    label: "Why your team stepped in",
    definition: "Sessions a person joined without the agent asking, by what prompted it.",
  },
  needsReview: {
    label: "Review queue",
    definition:
      "Verdicts waiting for a person: the review was unsure, could not tell why a handover happened, or a correction needs confirming.",
  },
  unresolvedTopics: {
    label: "Questions the agent didn't resolve",
    definition:
      "Requests the agent did not resolve alone, those your team resolved included, by kind of question. A session about two questions counts under each, and once in their kind.",
  },
}

/** Every cohort added back together — how sessions ended, whoever handled them. */
export function totalOutcomes(split: CohortOutcomeCounts): OutcomeCounts {
  return Object.fromEntries(
    SESSION_OUTCOMES.map((outcome) => [
      outcome,
      split.aiOnly[outcome] + split.teamInvolved[outcome] + split.unattended[outcome],
    ])
  ) as OutcomeCounts
}

/** Every judged session that was a request at all — the population both outcome rates share. */
export function requestCount(counts: OutcomeCounts): number {
  return REQUEST_OUTCOMES.reduce((sum, outcome) => sum + counts[outcome], 0)
}

/**
 * Resolved by the agent alone, out of every real request. A resolved verdict on a session the
 * team took part in is the team's success, whoever gave the verdict.
 */
export function selfServiceShare(split: CohortOutcomeCounts): number | null {
  return shareOf(selfServiceCounts(split))
}

export function selfServiceCounts(split: CohortOutcomeCounts): ShareCounts {
  return { of: resolvedCount(split.aiOnly), over: requestCount(totalOutcomes(split)) }
}

/** The same sessions, out of the requests left to the agent: no team, and not handed over. */
export function resolutionRate(split: CohortOutcomeCounts): number | null {
  return shareOf(resolutionCounts(split))
}

export function resolutionCounts(split: CohortOutcomeCounts): ShareCounts {
  const leftToAgent =
    requestCount(split.aiOnly) -
    split.aiOnly.escalated +
    requestCount(split.unattended) -
    split.unattended.escalated
  return { of: resolvedCount(split.aiOnly), over: leftToAgent }
}

/** Handed to team, out of every real request — the funnel's own segment. */
export function handedToTeamCounts(split: CohortOutcomeCounts): ShareCounts {
  const totals = totalOutcomes(split)
  return { of: totals.escalated, over: requestCount(totals) }
}

export function shareOf({ of, over }: ShareCounts): number | null {
  return share(of, over)
}

function resolvedCount(counts: OutcomeCounts): number {
  return RESOLVED_OUTCOMES.reduce((sum, outcome) => sum + counts[outcome], 0)
}
